import { Call, Device } from "@twilio/voice-sdk";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { getReservationsByTask, isCallTask, sendTrackingEvent } from "../../ActionUtils";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm/CbmImpl";
import { Action } from "~/modules/actions/Action";
import { VoiceCall } from "~/modules/VoiceCall/VoiceCall";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { VoiceCallImpl } from "~/modules/VoiceCall";




export interface GetCallByTaskOptions {
    voiceDevice?: Device;
}



















export class GetCallByTask implements Action<Promise<VoiceCall>> {
    


    #taskSid: string;

    


    #options?: GetCallByTaskOptions;

    constructor(taskSid: string, options?: GetCallByTaskOptions) {
        this.#taskSid = taskSid;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        const voiceController = context.retrieveInstanceOf(ContextInstance.VoiceControllerImpl, VoiceControllerImpl);
        if (this.#options?.voiceDevice) {
            await voiceController.init(this.#options.voiceDevice);
        }
        await cbm.init();

        logger.debug(`getCallByTask invoked with taskSid: ${this.#taskSid}`);

        const worker = taskRouter.worker;

        if (!worker) {
            const errorMsg = "getCallByTask: worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "pending" || res.status === "accepted");

        if (!reservation) {
            const errorMsg = `getCallByTask: Reservation for task ${this.#taskSid} not found`;

            sendTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice
            });

            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const taskCallSid = reservation.task.attributes?.conference?.participants?.worker;
        if (!isCallTask(reservation.task)) {
            const errorMsg = `getCallByTask: The task is not a voice task ${this.#taskSid}`;
            sendTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice
            });
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!taskCallSid) {
            const errorMsg = `getCallByTask: Failed to get conference data from task ${this.#taskSid}`;
            sendTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice
            });
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const activeCall: Call = voiceController.voiceDevice?.["_activeCall"];
        const isActiveTaskCall = activeCall?.parameters?.CallSid === taskCallSid;

        if (activeCall && isActiveTaskCall) {
            sendTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice,
                callSid: taskCallSid,
                isIncomingCall: false
            });
            return new VoiceCallImpl(context, activeCall, voiceController.voiceDevice) as VoiceCall;
        }

        const incomingCall = voiceController.voiceDevice?.calls?.find(
            (call) => call.parameters.CallSid === taskCallSid
        );
        if (incomingCall) {
            sendTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                customVoiceDevice: !!this.#options?.voiceDevice,
                callSid: taskCallSid,
                isIncomingCall: true
            });
            return new VoiceCallImpl(context, incomingCall, voiceController.voiceDevice) as VoiceCall;
        }

        const errorMsg = `getCallByTask: Failed to get call by task ${
            this.#taskSid
        }. The call might be active on a different voiceDevice`;
        logger.error(errorMsg);
        sendTrackingEvent(EVENTS.GetCallByTaskCompleted, analytics, worker, {
            error: errorMsg,
            taskSid: this.#taskSid,
            customVoiceDevice: !!this.#options?.voiceDevice,
            callSid: taskCallSid
        });
        return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
    }
}
